import { Link } from 'react-router-dom';
import { SRS_RATING_LABELS, SRS_RATINGS, type ReviewSummary } from '@en3/shared';
import { ROUTES } from '@/app/routes';
import { Button } from '@/components/ui/button';
import { Card, CardBody, CardHeader, CardTitle } from '@/components/ui/card';

/**
 * 本轮复习的结算页。
 *
 * 它要回答三个问题，顺序就是渲染顺序：
 * 1. **刚才发生了什么**——复习张数 / 记住 / 忘记 / 用时（`message` 是一句人话总结）；
 * 2. **我的自评都落在哪里**——五档的次数分布。这个分布比"正确率"更有信息量：
 *    全落在"有点模糊"说明卡片的提示给多了（或者词确实没学会）；
 * 3. **接下来做什么**——还有卡就"继续下一批"，没有就回生词本或去阅读。
 *
 * 分布条的宽度按**本档次数 / 本轮总数**取，而不是按最大值归一化：
 * 这样"记得 8 张"与"秒答 4 张"的条长比就是 2:1，读条形图时不会被相对比例误导。
 * 分母用 `Math.max(1, reviewed)`，避免 0 张时除出 NaN。
 */
function formatDuration(durationMs: number): string {
  const totalSeconds = Math.max(0, Math.round(durationMs / 1000));
  if (totalSeconds < 60) return `${totalSeconds} 秒`;
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return seconds === 0 ? `${minutes} 分钟` : `${minutes} 分 ${seconds} 秒`;
}

/** 下一次到期时间的人话说法（复习结束后最早到期的卡）。 */
function formatNextDue(nextDueAt: number | null): string {
  if (nextDueAt === null) return '这批词都排到更远的日期了';
  const diff = nextDueAt - Date.now();
  if (diff <= 0) return '马上就有下一张到期';
  const minutes = Math.round(diff / 60_000);
  if (minutes < 60) return `约 ${minutes} 分钟后`;
  const hours = Math.round(diff / 3_600_000);
  if (hours < 24) return `约 ${hours} 小时后`;
  const days = Math.round(diff / 86_400_000);
  return `约 ${days} 天后`;
}

export function ReviewSummaryView({
  summary,
  canContinue,
  onContinue,
  continuing = false,
}: {
  summary: ReviewSummary;
  /** 队列里还有卡可练（混合模式下会用"最不熟的"补齐） */
  canContinue: boolean;
  onContinue: () => void;
  continuing?: boolean;
}) {
  const denominator = Math.max(1, summary.reviewed);

  return (
    <Card className="mt-4">
      <CardHeader>
        <CardTitle>本轮复习完成</CardTitle>
      </CardHeader>
      <CardBody className="space-y-5">
        <p role="status" className="rounded-md border bg-muted/40 px-3 py-2 text-sm">
          {summary.message}
        </p>

        <dl className="grid grid-cols-2 gap-3 text-center sm:grid-cols-4">
          <Stat label="复习" value={`${summary.reviewed} 张`} />
          <Stat label="记住了" value={`${summary.recalled} 张`} />
          <Stat label="忘记了" value={`${summary.forgotten} 张`} />
          <Stat label="用时" value={formatDuration(summary.durationMs)} />
        </dl>

        <section>
          <h3 className="text-xs font-semibold tracking-wide text-muted-foreground uppercase">自评分布</h3>
          <ul className="mt-2 space-y-2">
            {SRS_RATINGS.map((rating) => {
              const count = summary.ratingCounts[rating] ?? 0;
              const percent = Math.round((count / denominator) * 100);
              return (
                <li key={rating} className="flex items-center gap-3 text-xs">
                  <span className="w-16 shrink-0 text-muted-foreground">{SRS_RATING_LABELS[rating]}</span>
                  <span className="h-2 flex-1 overflow-hidden rounded-full bg-muted">
                    <span className="block h-full rounded-full bg-primary" style={{ width: `${percent}%` }} />
                  </span>
                  <span className="w-14 shrink-0 text-right text-muted-foreground tabular-nums">
                    {count} 张 · {percent}%
                  </span>
                </li>
              );
            })}
          </ul>
        </section>

        <p className="text-xs text-muted-foreground">下次复习：{formatNextDue(summary.nextDueAt)}。</p>

        <div className="flex flex-wrap gap-2">
          {canContinue && (
            <Button onClick={onContinue} loading={continuing}>
              继续复习下一批
            </Button>
          )}
          <Link
            to={ROUTES.wordbook}
            className="inline-flex h-10 items-center rounded-md border bg-background px-4 text-sm hover:bg-accent hover:text-accent-foreground"
          >
            回生词本
          </Link>
          {!canContinue && (
            <Link
              to={ROUTES.textbooks}
              className="inline-flex h-10 items-center rounded-md px-4 text-sm text-muted-foreground hover:bg-accent hover:text-accent-foreground"
            >
              去读新文章
            </Link>
          )}
        </div>
      </CardBody>
    </Card>
  );
}

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-md border p-3">
      <dt className="text-xs text-muted-foreground">{label}</dt>
      <dd className="mt-1 text-lg font-semibold">{value}</dd>
    </div>
  );
}
